import { HorizBox } from 'component/basics';
import { CTAButton } from 'component/button';
import { DatastoreProvider, InstanceStack } from 'component/navigation/instance';
import { LoadingSpinnerContainer } from 'component/progress/loading-spinner';
import { useInstanceConfig } from 'data/instance-config';
import { Redirect, useLocalSearchParams } from 'expo-router';
import { getIsInPsiNativeAppWebview } from 'platform-specific/url';
import { profileModuleQuery } from 'system/structure-modules';
import { authClient, useSession } from 'util/auth-client';
import { fireAndForget } from 'util/hooks';
import { t } from 'util/stdtypes';

const screenOptions = { headerRight: () => <LogOutButton /> };

/**
 * Mounts the signed-in user's own profile, and sends anyone signed out to
 * login. Its header offers Log out in place of the account menu, which shows
 * only once there's a saved profile.
 */
export default function ProfileLayout() {
    const { siloKey } = useLocalSearchParams<'/[siloKey]/profile'>();
    const session = useSession();
    const profileKey = session.data?.user.id;

    if (session.isPending) {
        return <LoadingSpinnerContainer />;
    }

    if (!profileKey) {
        return (
            <Redirect
                href={{
                    pathname: '/[siloKey]/login/one',
                    params: { siloKey },
                }}
            />
        );
    }

    return <OwnProfileLayout profileKey={profileKey} siloKey={siloKey} />;
}

function OwnProfileLayout({ siloKey, profileKey }: { siloKey: string; profileKey: string }) {
    const instanceConfig = useInstanceConfig({
        siloKey,
        structureKey: 'profile',
        instanceKey: profileKey,
        moduleQuery: profileModuleQuery,
    });

    return (
        <DatastoreProvider {...instanceConfig}>
            <InstanceStack screenOptions={screenOptions} />
        </DatastoreProvider>
    );
}

function LogOutButton() {
    if (getIsInPsiNativeAppWebview()) {
        return null;
    }

    return (
        <HorizBox center>
            <CTAButton
                label={t('user_info.log_out_button', 'Log out')}
                onPress={() => fireAndForget(authClient.signOut())}
                size='small'
                type='secondary'
            />
        </HorizBox>
    );
}
